import React, { useEffect, useState, useCallback } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import VivApi from "../../../api";
import { useForm } from "react-hook-form";
import { getUniqueRes } from "../../../utils/api-utils";
import "./Search.css";

// onSearch(savedFilter) — optional. When provided, called after save so the
// parent page can fetch inline results instead of navigating away.

const EMPTY_FILTER = {
  minAge: null, maxAge: null,
  minHeight: null, maxHeight: null,
  maritalStatus: [1],
  religion: null, caste: [],
  motherTongue: null,
  state: [], city: [],
};

const agef = (from = 17) => {
  const opts = [];
  for (let i = from + 1; i <= 80; i++) opts.push({ value: i, label: String(i) });
  return opts;
};

const Search = ({ onSearch }) => {
  const { handleSubmit, setValue, watch, reset } = useForm({ defaultValues: EMPTY_FILTER });
  const watchval = watch();

  const [profile, setProfile] = useState({});
  const [caste, setCaste] = useState([]);
  const [city, setCity] = useState([]);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      const [profRes, filterRes, userRes] = await Promise.all([
        VivApi.profile.listProfile(),
        VivApi.filter.getFilter(),
        VivApi.Auth.userMe().catch(() => ({ data: {} })),
      ]);
      const pd = profRes.data;
      setProfile(pd);

      const fd = filterRes.data || {};

      // Default: Never married — only when not explicitly saved (preserve [] if user cleared it)
      if (fd.maritalStatus === undefined || fd.maritalStatus === null) fd.maritalStatus = [1];

      // Default: user's own religion (only when no religion filter has been saved)
      if (fd.religion == null) {
        const userReligionId = userRes.data?.user?.religionId ?? userRes.data?.religionId;
        if (userReligionId && pd.religions) {
          const match = pd.religions.find(r => String(r.sid) === String(userReligionId));
          if (match) fd.religion = match.sid;
        }
      }

      // Default age range based on logged-in user's gender (only when no age filter saved yet)
      if (fd.minAge == null && fd.maxAge == null) {
        const userDob = userRes.data?.user?.dateOfBirth ?? userRes.data?.dateOfBirth;
        const userGender = String(userRes.data?.user?.gender ?? userRes.data?.gender ?? '').trim().toLowerCase();
        if (userDob) {
          const dob = new Date(userDob);
          const today = new Date();
          let age = today.getFullYear() - dob.getFullYear();
          const mo = today.getMonth() - dob.getMonth();
          if (mo < 0 || (mo === 0 && today.getDate() < dob.getDate())) age--;
          if (userGender === 'male' || userGender === 'm') {
            // Male searching females: default max age = male's age - 1
            fd.maxAge = age - 1;
          } else if (userGender === 'female' || userGender === 'f') {
            // Female searching males: default min age = female's age
            fd.minAge = age;
          }
        }
      }

      if (pd.religions && fd.religion) {
        const rel = pd.religions.find((r) => r.sid === fd.religion);
        if (rel) setCaste(rel.castes || []);
      }
      if (pd.states && fd.state?.length) {
        const stateArr = pd.states.filter((s) => fd.state.includes(s.sid));
        setCity(stateArr.flatMap((s) => s.cities || []));
      }
      reset(fd);
    };
    init();
  }, [reset]);

  // Active filter chips derived from watchval
  const chips = [];
  if (watchval.minAge || watchval.maxAge)
    chips.push({ key: "age", label: `Age: ${watchval.minAge || "any"} – ${watchval.maxAge || "any"}` });
  if (watchval.minHeight || watchval.maxHeight) {
    const hLabel = (sid) => profile.heights?.find((h) => h.sid === sid)?.name || "any";
    chips.push({ key: "height", label: `Height: ${hLabel(watchval.minHeight)} – ${hLabel(watchval.maxHeight)}` });
  }
  if (watchval.maritalStatus?.length)
    chips.push({ key: "maritalStatus", label: `Marital: ${watchval.maritalStatus.map((s) => profile.maritalStatuses?.find((m) => m.sid === s)?.name || s).join(", ")}` });
  if (watchval.religion)
    chips.push({ key: "religion", label: `Religion: ${profile.religions?.find((r) => r.sid === watchval.religion)?.name || watchval.religion}` });
  if (watchval.caste?.length)
    chips.push({ key: "caste", label: `Caste: ${watchval.caste.map((c) => caste.find((x) => x.sid === c)?.name || c).join(", ")}` });
  if (watchval.motherTongue)
    chips.push({ key: "motherTongue", label: `Tongue: ${profile.motherTongues?.find((m) => m.sid === watchval.motherTongue)?.name || watchval.motherTongue}` });
  if (watchval.state?.length)
    chips.push({ key: "state", label: `State: ${watchval.state.map((s) => profile.states?.find((x) => x.sid === s)?.name || s).join(", ")}` });
  if (watchval.city?.length)
    chips.push({ key: "city", label: `City: ${watchval.city.map((c) => city.find((x) => x.sid === c)?.name || c).join(", ")}` });

  const removeChip = (key) => {
    const empties = { age: { minAge: null, maxAge: null }, height: { minHeight: null, maxHeight: null } };
    if (empties[key]) { Object.entries(empties[key]).forEach(([k, v]) => setValue(k, v)); }
    else if (key === "maritalStatus") setValue(key, []);
    else if (["caste", "state", "city"].includes(key)) setValue(key, []);
    else setValue(key, null);
  };

  const submitForm = async (data) => {
    setSaving(true);
    try {
      const newData = getUniqueRes(data);
      await VivApi.filter.saveFilter(newData);
      if (onSearch) {
        onSearch(newData);
      } else {
        navigate("/discover");
      }
    } catch (error) {
      console.error("Error Saving", error);
    } finally {
      setSaving(false);
    }
  };

  const resetAll = useCallback(() => {
    reset(EMPTY_FILTER);
    setCaste([]);
    setCity([]);
  }, [reset]);

  return (
    <form className="sf-form" onSubmit={handleSubmit(submitForm)}>

      {/* Active filter chips */}
      {chips.length > 0 && (
        <div className="sf-chips">
          {chips.map((c) => (
            <span key={c.key} className="sf-chip">
              {c.label}
              <button type="button" className="sf-chip-remove" onClick={() => removeChip(c.key)}>×</button>
            </span>
          ))}
          <button type="button" className="sf-chips-clear" onClick={resetAll}>Clear all</button>
        </div>
      )}

      {/* ── Basic ─────────────────────────────────────── */}
      <div className="sf-group">
        <div className="sf-group-title">Basic Info</div>

        <div className="sf-field">
          <label className="sf-label">Age</label>
          <div className="sf-pair">
            <Select
              options={agef(17)}
              onChange={(e) => setValue("minAge", e ? e.value : null)}
              value={agef(17).find((e) => e.value === watchval.minAge) || null}
              placeholder="Min age"
              isClearable
              classNamePrefix="sfsel"
            />
            <Select
              isDisabled={!watchval.minAge}
              options={agef(watchval.minAge || 17)}
              value={agef(17).find((e) => e.value === watchval.maxAge) || null}
              onChange={(e) => setValue("maxAge", e ? e.value : null)}
              placeholder="Max age"
              isClearable
              classNamePrefix="sfsel"
            />
          </div>
        </div>

        <div className="sf-field">
          <label className="sf-label">Height</label>
          <div className="sf-pair">
            <Select
              options={profile.heights}
              getOptionLabel={(o) => o.name}
              getOptionValue={(o) => o.sid}
              onChange={(e) => setValue("minHeight", e ? e.sid : null)}
              value={profile.heights?.find((e) => e.sid === watchval.minHeight) || null}
              placeholder="Min height"
              isClearable
              classNamePrefix="sfsel"
            />
            <Select
              options={profile.heights}
              getOptionLabel={(o) => o.name}
              getOptionValue={(o) => o.sid}
              onChange={(e) => setValue("maxHeight", e ? e.sid : null)}
              value={profile.heights?.find((e) => e.sid === watchval.maxHeight) || null}
              placeholder="Max height"
              isClearable
              classNamePrefix="sfsel"
            />
          </div>
        </div>

        <div className="sf-field">
          <label className="sf-label">Marital Status</label>
          <Select
            options={profile.maritalStatuses}
            getOptionLabel={(o) => o.name}
            getOptionValue={(o) => o.sid}
            onChange={(e) => setValue("maritalStatus", (e || []).map((x) => x.sid))}
            value={
              profile.maritalStatuses && watchval.maritalStatus
                ? watchval.maritalStatus.map((val) => profile.maritalStatuses.find((e) => e.sid === val)).filter(Boolean)
                : []
            }
            isMulti isClearable
            classNamePrefix="sfsel"
            placeholder="Any"
          />
        </div>
      </div>

      {/* ── Background ───────────────────────────────── */}
      <div className="sf-group">
        <div className="sf-group-title">Background</div>

        <div className="sf-field">
          <label className="sf-label">Religion</label>
          <Select
            options={profile.religions}
            getOptionLabel={(r) => r.name}
            getOptionValue={(r) => r.sid}
            isClearable
            onChange={(e) => {
              setValue("religion", e ? e.sid : null);
              setValue("caste", []);
              setCaste(e ? e.castes || [] : []);
            }}
            value={profile.religions?.find((e) => e.sid === watchval.religion) || null}
            classNamePrefix="sfsel"
            placeholder="Any religion"
          />
        </div>

        <div className="sf-field">
          <label className="sf-label">Caste</label>
          <Select
            options={caste}
            getOptionLabel={(o) => o.name}
            getOptionValue={(o) => o.sid}
            onChange={(e) => setValue("caste", e.map((x) => x.sid))}
            value={
              watchval.caste
                ? watchval.caste.map((val) => caste.find((e) => e.sid === val)).filter(Boolean)
                : []
            }
            isMulti isClearable
            isDisabled={!watchval.religion}
            classNamePrefix="sfsel"
            placeholder={watchval.religion ? "Any caste" : "Select religion first"}
          />
        </div>

        <div className="sf-field">
          <label className="sf-label">Mother Tongue</label>
          <Select
            options={profile.motherTongues}
            getOptionLabel={(o) => o.name}
            getOptionValue={(o) => o.sid}
            onChange={(e) => setValue("motherTongue", e ? e.sid : null)}
            isClearable
            value={profile.motherTongues?.find((e) => e.sid === watchval.motherTongue) || null}
            classNamePrefix="sfsel"
            placeholder="Any"
          />
        </div>
      </div>

      {/* ── Location ─────────────────────────────────── */}
      <div className="sf-group">
        <div className="sf-group-title">Location</div>

        <div className="sf-field">
          <label className="sf-label">State</label>
          <Select
            options={profile.states}
            getOptionLabel={(r) => r.name}
            getOptionValue={(r) => r.sid}
            onChange={(e) => {
              setValue("state", e.map((x) => x.sid));
              setValue("city", []);
              setCity(e.flatMap((x) => x.cities || []));
            }}
            value={
              profile.states && watchval.state
                ? watchval.state.map((val) => profile.states.find((e) => e.sid === val)).filter(Boolean)
                : []
            }
            isMulti isClearable
            classNamePrefix="sfsel"
            placeholder="Any state"
          />
        </div>

        <div className="sf-field">
          <label className="sf-label">City / District</label>
          <Select
            options={city}
            getOptionLabel={(o) => o.name}
            getOptionValue={(o) => o.sid}
            onChange={(e) => setValue("city", e.map((x) => x.sid))}
            value={
              watchval.city
                ? watchval.city.map((val) => city.find((e) => e.sid === val)).filter(Boolean)
                : []
            }
            isMulti isClearable
            isDisabled={!watchval.state?.length}
            classNamePrefix="sfsel"
            placeholder={watchval.state?.length ? "Any city" : "Select state first"}
          />
        </div>
      </div>

      {/* ── Actions ──────────────────────────────────── */}
      <div className="sf-actions">
        <button type="submit" className="sf-btn sf-btn--search" disabled={saving}>
          {saving ? "Saving…" : <><i className="ti ti-search" /> Search</>}
        </button>
        <button type="button" className="sf-btn sf-btn--reset" onClick={resetAll}>
          <i className="ti ti-reload" /> Reset
        </button>
      </div>

    </form>
  );
};

export default Search;
